import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CommonTable from "../../../components/Common/TableContainer";
import TaskBucketAddEditModal from "pages/AllCommonModal/TaskBucket/task-bucket-add-edit-modal";
import Spinners from "components/Common/Spinner";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useTaskBucketContext } from "libs/context/TaskBucketContext";
import { submitTaskBucketFilterData } from "model/TaskBucketModel";
import { timeAgo } from "helpers/GeneralHelper";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

/** Show `users: [{ id, name }]` as comma‑separated names in the table. */
const formatUsersForTable = (users) => {
  if (users == null || users === "") return "";
  if (Array.isArray(users)) {
    return users
      .map((u) => {
        if (u && typeof u === "object") {
          return String(u.name ?? u.userName ?? u.title ?? "").trim();
        }
        return String(u ?? "").trim();
      })
      .filter(Boolean)
      .join(", ");
  }
  if (typeof users === "string") return users;
  return String(users);
};

const normalizeTaskBucketRow = (row) => {
  if (!row || typeof row !== "object") return row;
  const usersRaw = Array.isArray(row?.users) ? row.users : [];
  return {
    ...row,
    updatedOn: timeAgo(row?.updatedOn),
    usersRaw,
    users: formatUsersForTable(usersRaw),
  };
};

const mapTaskBucketListPayload = (payload) => {
  const raw = payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : raw?.records ?? raw?.rows ?? [];
  return (Array.isArray(list) ? list : []).map(normalizeTaskBucketRow);
};

const TaskBucket = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchTaskBucketContextData, taskBucketContextData } = useTaskBucketContext() || {};
  const [taskBucketAddEditModalOpen, setTaskBucketAddEditModalOpen] = useState(false);
  const [modal1, setModal1] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [taskBucketData, setTaskBucketData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemId, setItemId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);

  const toggleViewModal = () => setModal1(!modal1);

  const filteredData = useMemo(() => {
    if (!searchQuery?.trim()) return taskBucketData;
    return taskBucketData.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [taskBucketData]);

  const taskBucketActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.MasterTaskBucketAccess),
      canEdit: Boolean(permissionData?.MasterTaskBucketAccess),
    }),
    [permissionData]
  );

  const hasAnyTaskBucketRowAction = useMemo(
    () => taskBucketActionPermissions.canEdit,
    [taskBucketActionPermissions]
  );

  useEffect(() => {
    fetchTaskBucketContextData?.();
    getTaskBucketList();
  }, []);

  useEffect(() => {
    const valueReModified = taskBucketContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: col?.field,
    }));
    setTableMetadata(valueReModified);
  }, [taskBucketContextData]);

  const getTaskBucketList = async () => {
    setLoading(true);
    const response = await submitTaskBucketFilterData({});

    if (response) {
      setTaskBucketData(mapTaskBucketListPayload(response));
    } else {
      setTaskBucketData([]);
    }
    setLoading(false);
  };

  const handleEdit = (row) => {
    const usersRaw = Array.isArray(row?.usersRaw) ? row.usersRaw : [];
    setEditRow({
      ...row,
      users: usersRaw,
    });
    setItemId(row?.id || row?._id);
    setTaskBucketAddEditModalOpen(true);
  };

  const handleFilterSubmit = async (data) => {
    setLoading(true);
    const response = await submitTaskBucketFilterData(data);

    if (response) {
      setTaskBucketData(mapTaskBucketListPayload(response));
    } else {
      setTaskBucketData([]);
    }
    setLoading(false);
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    setLoading(true);
    const payload = { search: value };
    const response = await submitTaskBucketFilterData(payload);

    if (response) {
      setTaskBucketData(mapTaskBucketListPayload(response));
    } else {
      setTaskBucketData([]);
    }
    setLoading(false);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Task Bucket</h5>

                {taskBucketActionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button
                      color="primary"
                      onClick={() => {
                        setItemId(null);
                        setEditRow(null);
                        setTaskBucketAddEditModalOpen(true);
                      }}
                    >
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && filteredData?.length && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyTaskBucketRowAction}
                  onEdit={
                    taskBucketActionPermissions.canEdit ? handleEdit : undefined
                  }
                  isEditAccess={taskBucketActionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={false}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"Task Bucket"}
                />
              ) : (
                // <Card className="border mb-3">
                //   <CardBody>
                //     <Spinners setLoading={setLoading} />
                //   </CardBody>
                // </Card>
                <Loaders />
              )}
            </CardBody>
          </Card>

          <TaskBucketAddEditModal
            setTaskBucketAddEditModalOpen={setTaskBucketAddEditModalOpen}
            taskBucketAddEditModalOpen={taskBucketAddEditModalOpen}
            getTaskBucketList={getTaskBucketList}
            itemId={itemId}
            editRow={editRow}
          />
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Task Bucket"
            handleSubmit={handleFilterSubmit}
            filterData={taskBucketContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

TaskBucket.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(TaskBucket);
